import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Droplet, Calculator, Car, ChevronRight } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './FuelConsumptionCalculatorPage/components/CalculationForm';
import ResultsOverview from './FuelConsumptionCalculatorPage/components/ResultsOverview';
import FuelComparisonChart from './FuelConsumptionCalculatorPage/components/FuelComparisonChart';
import QuickAdjustmentSidebar from './FuelConsumptionCalculatorPage/components/QuickAdjustmentSidebar';
import SavedVehiclesSidebar from './FuelConsumptionCalculatorPage/components/SavedVehiclesSidebar';
import FuelTipsSidebar from './FuelConsumptionCalculatorPage/components/FuelTipsSidebar';
import InfoContent from './FuelConsumptionCalculatorPage/components/InfoContent';
import AdditionalCalculators from './FuelConsumptionCalculatorPage/components/AdditionalCalculators';

// Определение начального состояния формы
export const initialFormData = {
  distance: '100',
  fuelConsumption: '8.5',
  fuelPrice: '54.50',
  fuelType: 'petrol-92',
  calculationType: 'consumption',
  distanceUnit: 'km',
  consumptionUnit: 'l_100km',
  priceUnit: 'rub_l',
  includeAdditionalCosts: false,
  additionalCosts: {
    maintenance: '0',
    insurance: '0',
    tax: '0',
    parking: '0',
    toll: '0',
    other: '0'
  },
  vehicleInfo: {
    make: '',
    model: '',
    year: '',
    engineVolume: '',
    fuelTankCapacity: '',
    averageSpeed: '60'
  },
  includeEcoInfo: false,
  ecoMode: {
    tireInflation: false,
    airFilter: false,
    airConditioner: false,
    cargoWeight: false,
    economicDriving: false
  },
  includeRouteInfo: false,
  routeInfo: {
    startCity: '',
    endCity: '',
    highwayPercentage: '70',
    cityPercentage: '30'
  }
};

// Типы топлива
export const fuelTypes = [
  { id: 'petrol-92', name: 'АИ-92', defaultPrice: 51.20, co2Factor: 2.31 },
  { id: 'petrol-95', name: 'АИ-95', defaultPrice: 54.50, co2Factor: 2.32 },
  { id: 'petrol-98', name: 'АИ-98', defaultPrice: 60.40, co2Factor: 2.33 },
  { id: 'petrol-100', name: 'АИ-100', defaultPrice: 65.00, co2Factor: 2.35 },
  { id: 'diesel', name: 'Дизель', defaultPrice: 57.80, co2Factor: 2.68 },
  { id: 'gas', name: 'Газ (пропан)', defaultPrice: 25.50, co2Factor: 1.63 },
  { id: 'gas-methane', name: 'Газ (метан)', defaultPrice: 23.90, co2Factor: 1.42 },
  { id: 'electricity', name: 'Электричество', defaultPrice: 4.50, co2Factor: 0.45, unit: 'кВт·ч' }
];

// Единицы измерения расхода топлива
export const consumptionUnits = [
  { id: 'l_100km', name: 'л/100 км', isDefault: true, defaultFor: ['ru', 'eu'] },
  { id: 'km_l', name: 'км/л', isDefault: false, defaultFor: [] },
  { id: 'mpg_us', name: 'MPG (US)', isDefault: false, defaultFor: ['us'] },
  { id: 'mpg_uk', name: 'MPG (UK)', isDefault: false, defaultFor: ['uk'] },
  { id: 'kwh_100km', name: 'кВт·ч/100 км', isDefault: false, defaultFor: [], forElectric: true }
];

// Единицы измерения расстояния
export const distanceUnits = [
  { id: 'km', name: 'км', isDefault: true, defaultFor: ['ru', 'eu'] },
  { id: 'miles', name: 'миль', isDefault: false, defaultFor: ['us', 'uk'] }
];

// Единицы измерения цены топлива
export const priceUnits = [
  { id: 'rub_l', name: '₽/л', isDefault: true, defaultFor: ['ru'] },
  { id: 'rub_km', name: '₽/км', isDefault: false, defaultFor: [] },
  { id: 'rub_kwh', name: '₽/кВт·ч', isDefault: false, defaultFor: [], forElectric: true }
];

/**
 * FuelConsumptionCalculatorPage Component - Полный калькулятор расхода топлива с различными параметрами и дополнительной информацией.
 */
const FuelConsumptionCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('fuel-consumption-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор расхода топлива | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте расход топлива, стоимость поездки и выбросы CO2. Сравните различные виды топлива и получите советы по экономичному вождению.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState('consumption'); // 'consumption', 'cost', 'eco'
  const [activeComparisonTab, setActiveComparisonTab] = useState('cost'); // 'cost', 'consumption', 'eco'

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных автомобилей из localStorage ---
  useEffect(() => {
    const savedVehiclesData = localStorage.getItem('savedVehicles');
    if (savedVehiclesData) {
      try {
        setSavedVehicles(JSON.parse(savedVehiclesData));
      } catch (e) {
        console.error("Error loading saved vehicles:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findFuelPrice = (id) => {
        const found = fuelTypes.find(t => t.id === id);
        return found ? found.defaultPrice.toString() : initialFormData.fuelPrice;
      };

      switch (subToolId) {
        case 'petrol-92':
          newState.fuelType = 'petrol-92';
          newState.fuelPrice = findFuelPrice('petrol-92');
          break;
        case 'petrol-95':
          newState.fuelType = 'petrol-95';
          newState.fuelPrice = findFuelPrice('petrol-95');
          break;
        case 'petrol-98':
          newState.fuelType = 'petrol-98';
          newState.fuelPrice = findFuelPrice('petrol-98');
          break;
        case 'diesel':
          newState.fuelType = 'diesel';
          newState.fuelPrice = findFuelPrice('diesel');
          break;
        case 'gas':
          newState.fuelType = 'gas';
          newState.fuelPrice = findFuelPrice('gas');
          break;
        case 'distance':
          newState.calculationType = 'distance';
          break;
        case 'cost':
          newState.calculationType = 'cost';
          break;
        case 'trip-planner':
          newState.includeRouteInfo = true;
          break;
        case 'eco-calculator':
          newState.includeEcoInfo = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setComparisonResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение автомобиля в localStorage
  const saveVehicle = (name) => {
    if (!results) return;

    const newSavedVehicle = {
      id: Date.now().toString(),
      name: name || `${formData.vehicleInfo.make} ${formData.vehicleInfo.model}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      fuelType: formData.fuelType
    };

    // Если не указаны марка и модель, используем тип топлива для имени
    if (!newSavedVehicle.name || newSavedVehicle.name === ' ') {
      const fuelTypeName = fuelTypes.find(t => t.id === formData.fuelType)?.name || formData.fuelType;
      newSavedVehicle.name = `Авто (${fuelTypeName})`;
    }

    const updatedSavedVehicles = [...savedVehicles, newSavedVehicle];
    setSavedVehicles(updatedSavedVehicles);
    localStorage.setItem('savedVehicles', JSON.stringify(updatedSavedVehicles));

    return newSavedVehicle;
  };

  // Удаление сохраненного автомобиля
  const deleteSavedVehicle = (id) => {
    const updatedSavedVehicles = savedVehicles.filter(vehicle => vehicle.id !== id);
    setSavedVehicles(updatedSavedVehicles);
    localStorage.setItem('savedVehicles', JSON.stringify(updatedSavedVehicles));
  };

  // Загрузка сохраненного автомобиля
  const loadSavedVehicle = (id) => {
    const vehicleToLoad = savedVehicles.find(vehicle => vehicle.id === id);
    if (vehicleToLoad) {
      setFormData(vehicleToLoad.formData);
      setResults(vehicleToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Droplet className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор расхода топлива'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setComparisonResults={setComparisonResults}
              fuelTypes={fuelTypes}
              consumptionUnits={consumptionUnits}
              distanceUnits={distanceUnits}
              priceUnits={priceUnits}
              saveVehicle={saveVehicle}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData}
                  setSelectedUnit={setSelectedUnit}
                  selectedUnit={selectedUnit}
                />
                
                {/* График сравнения видов топлива */}
                {comparisonResults && (
                  <FuelComparisonChart 
                    comparisonResults={comparisonResults}
                    activeTab={activeComparisonTab}
                    setActiveTab={setActiveComparisonTab}
                  />
                )}
                
                {/* Дополнительные калькуляторы */}
                <AdditionalCalculators 
                  results={results}
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент о расходе топлива --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              fuelTypes={fuelTypes}
            />

            {/* --- Панель сохраненных автомобилей --- */}
            <SavedVehiclesSidebar 
              savedVehicles={savedVehicles}
              deleteSavedVehicle={deleteSavedVehicle}
              loadSavedVehicle={loadSavedVehicle}
            />

            {/* --- Панель полезных советов --- */}
            <FuelTipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for charts */
        .chart-container {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .chart-container {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .chart-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .chart-container::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .chart-container::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .chart-container::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .chart-container::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .chart-container::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .chart-container::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default FuelConsumptionCalculatorPage;